import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { FiSettings } from 'react-icons/fi/index.js';

import { Skeleton } from '~/components/shadcn/skeleton.tsx';
import type { CleanNotificationWithRelations } from '~/database/supabase/notifications/types.ts';
import { getMostPopularTicker, getNotificationDate } from '~/modules/notification/utils.ts';
import { requireAuthenticatedBeforeLoad } from '~/routing/require-authenticated-before-load.ts';

import { NotificationsTableWrapper } from '../components/notifications/notifications-table.tsx';
import { getNotificationsPageDataQueryOptions } from '../server/notifications/notifications-page-data-query.ts';

export interface NotificationMergeType {
	readonly companyName: string | null | undefined;
	readonly date: Date | null;
	readonly description: string | null;
	readonly notificationId: number;
	readonly ticker: string;
	readonly type: string | null;
}

interface NotificationsSearch {
	readonly id?: string;
}

function validateNotificationsSearch(search: Record<string, unknown>): NotificationsSearch {
	return {
		id: typeof search.id === 'string' ? search.id : undefined,
	};
}

export const Route = createFileRoute('/notifications')({
	beforeLoad: requireAuthenticatedBeforeLoad,
	component: NotificationsPage,
	loader: ({ context }) => context.queryClient.ensureQueryData(getNotificationsPageDataQueryOptions()),
	pendingComponent: NotificationsPagePending,
	pendingMinMs: 250,
	validateSearch: validateNotificationsSearch,
});

function NotificationsPagePending() {
	return (
		<div className="h-full px-0 pt-1 md:px-1">
			<div className="flex h-full flex-col gap-y-2">
				<div className="flex justify-end px-2 py-2 align-end">
					<Skeleton className="size-8 rounded-sm border border-border bg-card" />
				</div>
				<div className="flex h-full justify-center px-2 pb-2">
					<div className="flex h-full w-full max-w-[1400px] flex-col gap-2 rounded-md border border-border bg-card p-3">
						<Skeleton className="h-10 w-full rounded-md" />
						<Skeleton className="h-10 w-full rounded-md" />
						<Skeleton className="h-10 w-full rounded-md" />
						<Skeleton className="h-full min-h-[320px] w-full rounded-md" />
					</div>
				</div>
			</div>
		</div>
	);
}

function NotificationsPage() {
	const { data } = useSuspenseQuery(getNotificationsPageDataQueryOptions());
	const { notifications, userLocale } = data;
	const { id } = Route.useSearch();

	return (
		<div className="h-full px-0 pt-1 md:px-1">
			<div className="flex h-full flex-col gap-y-2">
				<div className="flex justify-end px-2 py-2 align-end">
					<Link
						className="flex size-8 items-center justify-center rounded-sm border bg-accent"
						to="/user/notifications"
					>
						<FiSettings size={16} />
					</Link>
				</div>
				<div className="flex h-full justify-center">
					{notifications ? (
						<NotificationsTableWrapper
							mergedNotifications={mergeNotifications(notifications, userLocale)}
							notifications={notifications}
							searchId={id ?? null}
							userLocale={userLocale}
						/>
					) : null}
				</div>
			</div>
		</div>
	);
}

function mergeNotifications(
	notifications: CleanNotificationWithRelations[],
	userLocale: string,
): NotificationMergeType[] {
	return notifications.map((notification) => ({
		companyName: notification?.company?.name,
		date: getNotificationDate(notification),
		description: getNotificationDescription(notification, userLocale),
		notificationId: notification.id,
		ticker: getMostPopularTicker(notification.company?.instrument),
		type: getNotificationType(notification),
	}));
}

function getNotificationType(notification: CleanNotificationWithRelations): string | null {
	if (notification.calendar) {
		return 'Calendar';
	}

	if (notification.insider_transaction) {
		return 'Insider Transaction';
	}

	if (notification.news) {
		return 'News';
	}

	if (notification.short_register_aggregated) {
		return 'Short Register Aggregated';
	}

	if (notification.short_register_holder) {
		return 'Short Register Holder';
	}

	return null;
}

function getNotificationDescription(notification: CleanNotificationWithRelations, userLocale: string): string | null {
	if (notification.calendar?.date) {
		return `${new Date(notification.calendar.date).toLocaleDateString(userLocale)} - ${notification.calendar.description}`;
	}

	if (notification.insider_transaction) {
		const character = notification.insider_transaction.character?.toLowerCase() === 'avyttring' ? 'Sell' : 'Buy';
		const sum =
			parseFloat(notification.insider_transaction.volume ?? '') *
			parseFloat(notification.insider_transaction.price ?? '');

		return (
			character +
			' ' +
			notification.insider_transaction.instrument_name +
			' by ' +
			notification.insider_transaction.person_in_leading_position +
			', ' +
			sum.toLocaleString(userLocale)
		);
	}

	if (notification.news) {
		return notification.news.description;
	}

	if (notification.short_register_aggregated) {
		return `${notification.short_register_aggregated.name} - ${notification.short_register_aggregated.position_percent}%`;
	}

	if (notification.short_register_holder) {
		return `${notification.short_register_holder.holder} - ${notification.short_register_holder.position_percent}%`;
	}

	return null;
}
